import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CircleNotch, Shield, Warning, CheckCircle, XCircle, Desktop, Clock,
  Key, SignOut, DeviceMobile, ArrowsLeftRight, Trash, UserMinus, ArrowCounterClockwise, EnvelopeSimple, Users, Bug, CaretDown, UserPlus,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { toast } from 'sonner';
import { TransferOwnershipWizard } from '@/components/platform/TransferOwnershipWizard';
import { DeactivateUserDialog } from '@/components/platform/DeactivateUserDialog';
import { MIN_PASSWORD_LENGTH } from '@/lib/passwordPolicy';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

interface UserDetail {
  id: string; email: string; firstName: string; lastName: string; phone?: string;
  memberType: string; status: string; jobTitle?: string; twoFaEnabled: boolean;
  lastLoginAt?: string; lastLoginIp?: string; passwordChangedAt?: string; failedLoginAttempts: number;
  invitedAt?: string; inviteEmailStatus?: string | null; createdAt: string; updatedAt: string;
  deactivatedAt?: string | null; deactivationReason?: string | null;
  deactivationSource?: 'self' | 'chambers' | 'platform' | null; isBarrister: boolean;
  chambers: { id: string; name: string; slug: string; status: string; plan: string } | null;
}
interface LoginEvent { id: string; action: string; ip?: string; userAgent?: string; createdAt: string }
interface RelUser { userId: string; name: string; email: string; memberType: string; role: string; isActive: boolean; status: string }
interface Relationships {
  kind: 'barrister' | 'clerk' | 'other';
  clerks?: RelUser[];
  barristers?: (RelUser & { barristerId: string })[];
  workload?: { activeMatters: number };
}
interface TimelineEntry {
  id: string; action: string; actorEmail?: string | null;
  metadata?: Record<string, unknown> | null; createdAt: string;
}
interface UserDetailResponse {
  user: UserDetail;
  security: { activeSessionCount: number; loginEvents: LoginEvent[]; failedLoginAttempts: number; mfaEnabled: boolean; passwordChangedAt?: string; lockedUntil?: string | null };
  relationships: Relationships;
  permissions: string[];
  roleNames: string[];
  operationsTimeline: TimelineEntry[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (s?: string | null) => (!s ? '—' : new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
const fmtDate = (s?: string | null) => (!s ? '—' : new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));

const TIMELINE_LABELS: Record<string, string> = {
  'user.create': 'Account created', 'user.invite_resend': 'Invitation resent',
  'user.suspend': 'Suspended', 'user.reactivate': 'Reactivated',
  'user.reset_password': 'Password reset', 'user.reset_mfa': 'MFA reset',
  'user.force_logout': 'Force logged out', 'user.transfer_ownership': 'Ownership transferred',
  'user.deactivate': 'Deactivated', 'user.bulk': 'Bulk action',
  // History written before account deletion was retired (CLAUDE.md #25) — the account was only ever
  // hidden and preserved, so it is shown in the product vocabulary.
  'user.delete': 'Deactivated', 'user.restore': 'Reactivated',
};

const DEACTIVATION_SOURCE_LABELS: Record<string, string> = {
  self: 'by the user',
  chambers: 'by a chambers administrator',
  platform: 'from the Platform Console',
};

function MemberTypeBadge({ type }: { type: string }) {
  const map: Record<string, string> = {
    admin: 'bg-plat-purple-soft text-plat-purple-text', silk: 'bg-plat-info-soft text-plat-info-text',
    junior: 'bg-plat-info-soft text-plat-info-text', pupil: 'bg-plat-info-soft text-plat-info-text',
    senior_clerk: 'bg-plat-teal-soft text-plat-teal-text', junior_clerk: 'bg-plat-teal-soft text-plat-teal-text',
    solicitor: 'bg-plat-orange-soft text-plat-orange-text',
  };
  const label = type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${map[type] ?? 'bg-plat-neutral-soft text-plat-neutral-text'}`}>{label}</span>;
}
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-plat-success-soft text-plat-success-text', invited: 'bg-plat-warning-soft text-plat-warning-text',
    suspended: 'bg-plat-danger-soft text-plat-danger-text', inactive: 'bg-plat-neutral-soft text-plat-neutral-text',
  };
  // Product vocabulary: Active → Deactivated → Reactivated. `inactive` is a deactivated account.
  const labels: Record<string, string> = { active: 'Active', invited: 'Invited', suspended: 'Suspended', inactive: 'Deactivated' };
  return <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${map[status] ?? 'bg-plat-neutral-soft text-plat-neutral-text'}`}>{labels[status] ?? status}</span>;
}

function Card({ title, icon, children, right }: { title: string; icon?: React.ReactNode; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-plat-fg">{icon}{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-plat-faint">{label}</dt>
      <dd className="text-right text-plat-fg">{children}</dd>
    </div>
  );
}

// ── Diagnostics card — content-free error telemetry (Phase A) ──────────────────

interface DiagnosticEvent {
  id: string;
  kind: string; // 'server_error' | 'client_error'
  correlationId: string | null;
  statusCode: number;
  errorCode: string | null;
  method: string | null;
  path: string | null;
  message: string | null;
  stack: string | null;
  createdAt: string;
}

function DiagnosticsCard({ userId }: { userId: string }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'diagnostics', 'user', userId],
    queryFn: async () => {
      const res = await platformApi.get(`/diagnostics`, { params: { userId, limit: 25 } });
      return (res.data?.data ?? res.data) as { events: DiagnosticEvent[] };
    },
    enabled: !!userId,
  });
  const events = data?.events ?? [];

  return (
    <Card
      title="Recent errors"
      icon={<Bug className="h-4 w-4 text-plat-warning-text" />}
      right={<span className="text-xs text-plat-faint">{events.length ? `${events.length} shown` : ''}</span>}
    >
      <p className="mb-3 text-xs text-plat-faint">
        Server (5xx) and client (UI) errors this account hit (last 25). Diagnostics only — status,
        route and stack, never message content, matters, documents or mailbox data.
      </p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-plat-faint"><CircleNotch className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : error ? (
        <div className="text-sm text-plat-danger-text">{getPlatformApiErrorMessage(error)}</div>
      ) : events.length === 0 ? (
        <div className="rounded-lg border border-dashed border-plat-border p-4 text-center text-sm text-plat-success-text">
          No recent errors for this account.
        </div>
      ) : (
        <div className="space-y-1.5">
          {events.map((e) => (
            <div key={e.id} className="rounded-lg border border-plat-border bg-plat-surface">
              <button
                onClick={() => setOpenId(openId === e.id ? null : e.id)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-xs"
              >
                {e.kind === 'client_error' ? (
                  <span className="rounded bg-plat-warning-soft px-1.5 py-0.5 font-mono font-semibold text-plat-warning-text">UI</span>
                ) : (
                  <span className="rounded bg-plat-danger-soft px-1.5 py-0.5 font-mono font-semibold text-plat-danger-text">{e.statusCode}</span>
                )}
                <span className="font-mono text-plat-muted">{e.kind === 'client_error' ? e.errorCode : e.method}</span>
                <span className="flex-1 truncate text-plat-secondary">{e.path}</span>
                <span className="text-plat-faint">{fmt(e.createdAt)}</span>
                <CaretDown className={`h-3.5 w-3.5 text-plat-faint transition ${openId === e.id ? 'rotate-180' : ''}`} />
              </button>
              {openId === e.id && (
                <div className="border-t border-plat-border px-3 py-2 text-xs">
                  <div className="mb-1"><span className="text-plat-faint">Code:</span> <span className="text-plat-secondary">{e.errorCode ?? '—'}</span></div>
                  {e.correlationId && <div className="mb-1"><span className="text-plat-faint">Trace:</span> <span className="font-mono text-plat-muted">{e.correlationId}</span></div>}
                  {e.message && <div className="mb-1 text-plat-secondary">{e.message}</div>}
                  {e.stack && <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-plat-scrim/60 p-2 text-[10px] leading-relaxed text-plat-faint">{e.stack}</pre>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Barrister ↔ clerk relationship manager (Phase 2) ──────────────────────────

interface ClerkLite { id: string; firstName: string; lastName: string; email: string; memberType: string; role?: string }

function BarristerClerksManager({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [pickerClerk, setPickerClerk] = useState('');
  const [pickerRole, setPickerRole] = useState<'assistant' | 'primary'>('assistant');
  const [removing, setRemoving] = useState<ClerkLite | null>(null);

  const assignedQ = useQuery({
    queryKey: ['platform', 'barrister-clerks', userId],
    queryFn: async () => {
      const res = await platformApi.get(`/users/${userId}/clerks`);
      return ((res.data?.data ?? res.data) as { clerks: ClerkLite[] }).clerks;
    },
  });
  const availableQ = useQuery({
    queryKey: ['platform', 'barrister-clerks', userId, 'available'],
    queryFn: async () => {
      const res = await platformApi.get(`/users/${userId}/clerks/available`);
      return ((res.data?.data ?? res.data) as { clerks: ClerkLite[] }).clerks;
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['platform', 'barrister-clerks', userId] });
    qc.invalidateQueries({ queryKey: ['platform', 'user', userId] });
  };

  const assignMut = useMutation({
    mutationFn: () => platformApi.post(`/users/${userId}/clerks`, { clerkUserId: pickerClerk, role: pickerRole }),
    onSuccess: () => { toast.success('Clerk assigned'); setPickerClerk(''); setPickerRole('assistant'); refresh(); },
    onError: (e) => toast.error(getPlatformApiErrorMessage(e)),
  });
  const removeMut = useMutation({
    mutationFn: (clerkUserId: string) => platformApi.delete(`/users/${userId}/clerks/${clerkUserId}`),
    onSuccess: () => { toast.success('Clerk removed'); setRemoving(null); refresh(); },
    onError: (e) => toast.error(getPlatformApiErrorMessage(e)),
  });

  const assigned = assignedQ.data ?? [];
  const available = availableQ.data ?? [];

  return (
    <div className="mt-4">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-plat-faint">Clerks supporting this barrister</p>
      <p className="mb-2 text-[11px] text-plat-faint">
        Operational support relationship only — grants the clerk coordination visibility, never privileged legal content.
      </p>

      {assigned.length === 0 ? (
        <p className="text-sm text-plat-faint">No clerks assigned.</p>
      ) : (
        <div className="space-y-1.5">
          {assigned.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border border-plat-border bg-plat-surface px-3 py-2 text-sm">
              <Link to={`/platform/users/${c.id}`} className="text-plat-secondary hover:text-plat-info-text hover:underline">
                {c.firstName} {c.lastName}
              </Link>
              <div className="flex items-center gap-3">
                <span className="text-xs text-plat-faint capitalize">{c.memberType.replace('_', ' ')}{c.role ? ` · ${c.role}` : ''}</span>
                <button onClick={() => setRemoving(c)} title="Remove clerk" className="text-plat-faint transition hover:text-plat-danger-text"><Trash className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign */}
      <div className="mt-3 flex items-center gap-2">
        <select
          value={pickerClerk}
          onChange={(e) => setPickerClerk(e.target.value)}
          className="h-9 flex-1 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-secondary focus:border-plat-focus focus:outline-none"
        >
          <option value="">{available.length ? 'Select a clerk to assign…' : 'No available clerks in this chambers'}</option>
          {available.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.memberType.replace('_', ' ')}</option>)}
        </select>
        <select
          value={pickerRole}
          onChange={(e) => setPickerRole(e.target.value as 'assistant' | 'primary')}
          className="h-9 rounded-lg border border-plat-border bg-plat-surface px-2 text-sm text-plat-secondary focus:border-plat-focus focus:outline-none"
        >
          <option value="assistant">Assistant</option>
          <option value="primary">Primary</option>
        </select>
        <button
          onClick={() => assignMut.mutate()}
          disabled={!pickerClerk || assignMut.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-3 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
        >
          {assignMut.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Assign
        </button>
      </div>

      {removing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 px-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-sm rounded-xl border border-plat-border bg-plat-surface p-6 shadow-2xl">
            <h3 className="mb-2 text-[15px] font-semibold text-plat-fg">Remove clerk?</h3>
            <p className="mb-5 text-sm text-plat-muted">
              <span className="text-plat-fg">{removing.firstName} {removing.lastName}</span> will lose operational visibility of this barrister's work. This is reversible.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRemoving(null)} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-muted hover:bg-plat-hover">Cancel</button>
              <button onClick={() => removeMut.mutate(removing.id)} disabled={removeMut.isPending} className="inline-flex items-center gap-1.5 rounded-lg bg-plat-danger-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-danger-solid-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled">
                {removeMut.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Trash className="h-4 w-4" />} Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PlatformUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reasonFor, setReasonFor] = useState<null | 'suspend'>(null);
  const [reason, setReason] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [setPwOpen, setSetPwOpen] = useState(false);
  const [newPw, setNewPw] = useState('');

  const { data, isLoading, error } = useQuery<UserDetailResponse>({
    queryKey: ['platform', 'users', id],
    queryFn: async () => {
      const res = await platformApi.get(`/users/${id}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!id,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['platform', 'users'] });
  const onOk = (msg: string) => () => { toast.success(msg); invalidate(); };
  const onErr = (e: unknown) => toast.error(getPlatformApiErrorMessage(e));

  const suspend = useMutation({ mutationFn: () => platformApi.post(`/users/${id}/suspend`, { reason: reason.trim() }), onSuccess: onOk('User suspended and signed out'), onError: onErr });
  // Single step: a suspended or deactivated account comes back fully active (Phase 4-D rule L1).
  const reactivate = useMutation({ mutationFn: () => platformApi.post(`/users/${id}/reactivate`, {}), onSuccess: onOk('Account reactivated — the user can sign in again'), onError: onErr });
  const resendInvite = useMutation({ mutationFn: () => platformApi.post(`/users/${id}/resend-invite`, {}), onSuccess: onOk('Invitation resent'), onError: onErr });
  const resetPassword = useMutation({ mutationFn: () => platformApi.post(`/users/${id}/reset-password`, {}), onSuccess: onOk('Password reset email sent'), onError: onErr });
  const setPasswordDirect = useMutation({
    mutationFn: () => platformApi.post(`/users/${id}/set-password`, { password: newPw }),
    onSuccess: () => { toast.success('Password set — the user has been signed out and must use the new password'); setSetPwOpen(false); setNewPw(''); invalidate(); },
    onError: onErr,
  });
  const forceLogout = useMutation({ mutationFn: () => platformApi.post(`/users/${id}/force-logout`, {}), onSuccess: onOk('All sessions signed out'), onError: onErr });
  const resetMfa = useMutation({ mutationFn: () => platformApi.post(`/users/${id}/reset-mfa`, {}), onSuccess: onOk('MFA reset'), onError: onErr });

  if (isLoading) return <div className="flex items-center justify-center py-24"><CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" /></div>;
  if (error || !data) return <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-5 text-sm text-plat-danger-text">{getPlatformApiErrorMessage(error ?? new Error('User not found'))}</div>;

  const { user, security, relationships, permissions, roleNames, operationsTimeline } = data;
  const isDeactivated = user.status === 'inactive';
  const fullName = `${user.firstName} ${user.lastName}`.trim();

  const secBtn = 'inline-flex items-center gap-1.5 rounded-lg border border-plat-border bg-plat-surface px-3 py-1.5 text-xs font-medium text-plat-secondary transition hover:bg-plat-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled';

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <div>
        <button onClick={() => navigate('/platform/users')} className="mb-4 inline-flex items-center gap-1.5 text-sm text-plat-faint hover:text-plat-secondary">
          <ArrowLeft className="h-4 w-4" /> Users
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-plat-hover text-[18px] font-bold text-plat-secondary">
              {user.firstName[0]}{user.lastName[0]}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-plat-fg">{fullName}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="text-sm text-plat-muted">{user.email}</span>
                <MemberTypeBadge type={user.memberType} />
                <StatusBadge status={user.status} />
              </div>
            </div>
          </div>

          {/* Primary lifecycle actions */}
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {user.status === 'invited' && (
              <button onClick={() => resendInvite.mutate()} disabled={resendInvite.isPending} className={secBtn}>
                <EnvelopeSimple className="h-3.5 w-3.5" /> Resend invite
              </button>
            )}
            {(user.status === 'active' || user.status === 'invited') && (
              <button onClick={() => { setReason(''); setReasonFor('suspend'); }} className="rounded-lg border border-plat-danger-line bg-plat-danger-soft px-4 py-2 text-sm font-medium text-plat-danger-text transition hover:bg-plat-danger-soft">Suspend</button>
            )}
            {user.status === 'suspended' && (
              <button onClick={() => reactivate.mutate()} disabled={reactivate.isPending} className="rounded-lg border border-plat-success-line bg-plat-success-soft px-4 py-2 text-sm font-medium text-plat-success-text transition hover:bg-plat-success-soft">Reactivate</button>
            )}
          </div>
        </div>
      </div>

      {/* Deactivated banner — the account is preserved and can be reactivated in one step */}
      {isDeactivated && (
        <div className="rounded-xl border border-plat-warning-line bg-plat-warning-soft p-5">
          <div className="flex items-start gap-3">
            <UserMinus className="mt-0.5 h-5 w-5 shrink-0 text-plat-warning-text" />
            <div className="flex-1">
              <div className="font-semibold text-plat-fg">This account is deactivated</div>
              <div className="mt-1 text-sm text-plat-muted">
                {user.deactivatedAt ? `Deactivated ${fmt(user.deactivatedAt)}` : 'Deactivated'}
                {user.deactivationSource ? ` ${DEACTIVATION_SOURCE_LABELS[user.deactivationSource] ?? ''}` : ''}
                {user.deactivationReason ? ` — "${user.deactivationReason}"` : ''}.
                {' '}Access is switched off. The account and everything linked to it are preserved.
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => reactivate.mutate()} disabled={reactivate.isPending} className="inline-flex items-center gap-1.5 rounded-lg bg-plat-success-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-success-solid-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled">
                  {reactivate.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <ArrowCounterClockwise className="h-4 w-4" />} Reactivate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reason (suspend) inline dialog */}
      {reasonFor === 'suspend' && (
        <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
          <div className="flex items-start gap-3">
            <Warning className="mt-0.5 h-5 w-5 shrink-0 text-plat-warning-text" weight="fill" />
            <div className="flex-1">
              <div className="font-semibold text-plat-fg">Suspend this account?</div>
              <div className="mt-1 text-sm text-plat-muted">{fullName} will be immediately signed out of all sessions and temporarily blocked from logging in. Use Suspend for temporary blocks (e.g. security investigation) and Deactivate for permanent departures.</div>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="mt-3 h-9 w-full rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none" />
              <div className="mt-4 flex gap-2">
                <button onClick={() => suspend.mutate(undefined, { onSuccess: () => setReasonFor(null) })} disabled={reason.trim().length < 3 || suspend.isPending} className="rounded-lg bg-plat-danger-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-danger-solid-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled">
                  {suspend.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : 'Suspend'}
                </button>
                <button onClick={() => setReasonFor(null)} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-muted hover:bg-plat-hover">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Profile */}
        <Card title="Profile">
          <dl className="space-y-3 text-sm">
            <Row label="Email">{user.email}</Row>
            <Row label="Job title">{user.jobTitle || '—'}</Row>
            <Row label="Phone">{user.phone || '—'}</Row>
            <Row label="Member since">{fmtDate(user.createdAt)}</Row>
            <Row label="Invited">{fmtDate(user.invitedAt)}</Row>
            {user.inviteEmailStatus === 'failed' && (
              <Row label="Invitation"><span className="text-plat-warning-text">Email failed</span></Row>
            )}
            <Row label="2FA">
              {user.twoFaEnabled
                ? <span className="inline-flex items-center gap-1.5 text-plat-success-text"><CheckCircle className="h-4 w-4" weight="fill" />Enabled</span>
                : <span className="inline-flex items-center gap-1.5 text-plat-faint"><XCircle className="h-4 w-4" weight="fill" />Not enabled</span>}
            </Row>
          </dl>
        </Card>

        {/* Chambers + relationships */}
        <Card title="Chambers & relationships" icon={<Users className="h-4 w-4 text-plat-faint" />}>
          {user.chambers ? (
            <dl className="space-y-3 text-sm">
              <Row label="Chambers"><Link to={`/platform/tenants/${user.chambers.id}`} className="text-plat-info-text hover:underline">{user.chambers.name}</Link></Row>
              <Row label="Plan"><span className="capitalize">{user.chambers.plan}</span></Row>
            </dl>
          ) : <p className="text-sm text-plat-faint">No organisation linked.</p>}

          {relationships.kind === 'barrister' && <BarristerClerksManager userId={user.id} />}
          {relationships.kind === 'clerk' && (
            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-plat-faint">Barristers supported</p>
                {relationships.workload && <span className="text-xs text-plat-faint">{relationships.workload.activeMatters} active matters</span>}
              </div>
              {(relationships.barristers ?? []).length === 0 ? <p className="text-sm text-plat-faint">No barristers assigned.</p> : (
                <div className="space-y-1.5">
                  {relationships.barristers!.map((b) => (
                    <Link key={b.userId} to={`/platform/users/${b.userId}`} className="flex items-center justify-between rounded-lg border border-plat-border bg-plat-surface px-3 py-2 text-sm hover:border-plat-border-strong">
                      <span className="text-plat-secondary">{b.name}</span>
                      <span className="text-xs text-plat-faint capitalize">{b.memberType}{!b.isActive && ' · inactive'}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Security */}
        <Card title="Security" icon={<Shield className="h-4 w-4 text-plat-faint" />}
          right={<span className="inline-flex items-center gap-1.5 text-xs text-plat-faint"><Desktop className="h-3.5 w-3.5" />{security.activeSessionCount} active</span>}>
          <dl className="space-y-3 text-sm">
            <Row label="MFA">{security.mfaEnabled ? <span className="text-plat-success-text">Enabled</span> : <span className="text-plat-faint">Off</span>}</Row>
            <Row label="Password changed">{fmt(security.passwordChangedAt)}</Row>
            <Row label="Failed logins">{security.failedLoginAttempts}</Row>
            <Row label="Last login">{fmt(user.lastLoginAt)}{user.lastLoginIp ? ` · ${user.lastLoginIp}` : ''}</Row>
          </dl>
          {!isDeactivated && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-plat-border pt-4">
              <button onClick={() => resetPassword.mutate()} disabled={resetPassword.isPending} className={secBtn}><Key className="h-3.5 w-3.5" /> Email reset link</button>
              <button onClick={() => { setNewPw(''); setSetPwOpen(true); }} className={secBtn}><Key className="h-3.5 w-3.5" /> Set password</button>
              <button onClick={() => forceLogout.mutate()} disabled={forceLogout.isPending} className={secBtn}><SignOut className="h-3.5 w-3.5" /> Force logout</button>
              <button onClick={() => resetMfa.mutate()} disabled={resetMfa.isPending} className={secBtn}><DeviceMobile className="h-3.5 w-3.5" /> Reset MFA</button>
            </div>
          )}
          {security.loginEvents.length > 0 && (
            <div className="mt-4 border-t border-plat-border pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-plat-faint">Recent auth events</p>
              <div className="space-y-1.5">
                {security.loginEvents.slice(0, 5).map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-xs">
                    <span className={e.action === 'failed_login' ? 'text-plat-danger-text' : e.action === 'login' ? 'text-plat-success-text' : 'text-plat-muted'}>
                      {e.action.replace('_', ' ')}
                    </span>
                    <span className="text-plat-faint">{fmt(e.createdAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Diagnostics — content-free error telemetry for support */}
        <DiagnosticsCard userId={user.id} />

        {/* Permissions */}
        <Card title="Effective permissions" icon={<Key className="h-4 w-4 text-plat-faint" />}>
          {roleNames.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {roleNames.map((r) => <span key={r} className="rounded-md bg-plat-info-soft px-2 py-0.5 text-xs text-plat-info-text">{r}</span>)}
            </div>
          )}
          {permissions.length === 0 ? (
            <p className="text-sm text-plat-faint">No permissions yet (wired on first login).</p>
          ) : (
            <div className="flex max-h-48 flex-wrap gap-1.5 overflow-y-auto">
              {permissions.map((p) => <span key={p} className="rounded-md bg-plat-surface px-2 py-0.5 font-mono text-[11px] text-plat-muted">{p}</span>)}
            </div>
          )}
          <p className="mt-3 text-[11px] text-plat-faint">Read-only. Permission editing lives in the chambers workspace.</p>
        </Card>
      </div>

      {/* Operations timeline */}
      <Card title="Operations timeline" icon={<Clock className="h-4 w-4 text-plat-faint" />}>
        {operationsTimeline.length === 0 ? (
          <p className="text-sm text-plat-faint">No operations recorded yet.</p>
        ) : (
          <div className="divide-y divide-plat-border">
            {operationsTimeline.map((t) => {
              const meta = (t.metadata ?? {}) as Record<string, unknown>;
              const reasonTxt = typeof meta.reason === 'string' ? meta.reason : null;
              return (
                <div key={t.id} className="flex items-start justify-between gap-4 py-2.5">
                  <div>
                    <div className="text-sm text-plat-fg">{TIMELINE_LABELS[t.action] ?? t.action}</div>
                    <div className="text-xs text-plat-faint">
                      {t.actorEmail ?? 'system'}{reasonTxt ? ` — "${reasonTxt}"` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 text-xs text-plat-faint">{fmt(t.createdAt)}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Account access — transfer ownership (optional) and deactivate. Account deletion does not exist. */}
      {!isDeactivated && (
        <Card title="Account access" icon={<Warning className="h-4 w-4 text-plat-warning-text" />}>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setTransferOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-plat-border bg-plat-surface px-4 py-2 text-sm font-medium text-plat-secondary hover:bg-plat-hover">
              <ArrowsLeftRight className="h-4 w-4" /> Transfer ownership
            </button>
            <button onClick={() => setDeactivateOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-plat-warning-line bg-plat-warning-soft px-4 py-2 text-sm font-medium text-plat-warning-text hover:bg-plat-warning-soft">
              <UserMinus className="h-4 w-4" /> Deactivate account
            </button>
          </div>
          <p className="mt-3 text-xs text-plat-faint">Deactivation is for permanent departures. It switches access off and preserves the account and everything it owns. It can be reversed with Reactivate.</p>
        </Card>
      )}

      {/* Set-password modal — operator types the new password directly */}
      {setPwOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-plat-scrim/60 p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-md rounded-2xl border border-plat-border bg-plat-surface p-5 shadow-2xl">
            <h3 className="flex items-center gap-2 text-base font-semibold text-plat-fg"><Key className="h-4 w-4 text-plat-info-text" /> Set password for {fullName}</h3>
            <p className="mt-2 text-sm text-plat-muted">
              Sets this user's password immediately — including a chambers administrator. All their sessions are signed out and they must sign in again with the new password. Share it with them over a secure channel.
            </p>
            <input
              type="text"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder={`New password (min ${MIN_PASSWORD_LENGTH} characters)`}
              autoComplete="new-password"
              className="mt-3 h-9 w-full rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setSetPwOpen(false)} className="rounded-lg border border-plat-border px-4 py-2 text-sm text-plat-muted hover:bg-plat-hover">Cancel</button>
              <button disabled={newPw.length < MIN_PASSWORD_LENGTH || setPasswordDirect.isPending} onClick={() => setPasswordDirect.mutate()} className="inline-flex items-center gap-1.5 rounded-lg bg-plat-accent-solid px-4 py-2 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:bg-plat-neutral-soft disabled:text-plat-disabled">
                {setPasswordDirect.isPending ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />} Set password
              </button>
            </div>
          </div>
        </div>
      )}

      {user.chambers && (
        <>
          <TransferOwnershipWizard open={transferOpen} onClose={() => setTransferOpen(false)} user={{ id: user.id, chambersId: user.chambers.id, name: fullName }} onDone={invalidate} />
          <DeactivateUserDialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)} user={{ id: user.id, chambersId: user.chambers.id, name: fullName }} onDeactivated={() => queryClient.invalidateQueries({ queryKey: ['platform', 'users', id] })} />
        </>
      )}
    </div>
  );
}
